/**
 * Two hand-authored rooms.
 *
 * A first-time player must be able to *be* pranked before they are asked to
 * prank anyone — the fantasy is "I could design a better one than this", and
 * that thought only arrives after you've been had once. So these two are built
 * to teach the vocabulary in order: the first room teaches "the visible threat
 * is not the real one", the second teaches "the room can carry you".
 */

import type { PropTypeId } from '../core/props'
import type { PropInstance, RoomDef, Rot } from '../core/room'
import { ROOM_CONFIG, ROOM_VERSION } from '../core/room'
import { seedFromString } from '../core/rng'

type Spec = [PropTypeId, number, number, Rot?]

function build(
  id: string,
  name: string,
  ownerName: string,
  taunt: string,
  chest: { x: number; z: number },
  specs: Spec[],
): RoomDef {
  const props: PropInstance[] = specs.map(([type, x, z, rot], i) => ({
    iid: i + 1,
    type,
    x,
    z,
    rot: rot ?? 0,
  }))
  return {
    v: ROOM_VERSION,
    id,
    name,
    ownerName,
    taunt,
    seed: seedFromString(id),
    width: ROOM_CONFIG.width,
    depth: ROOM_CONFIG.depth,
    props,
    chest,
    door: { x: 6, z: 0 },
    createdAt: 0,
  }
}

/**
 * Room 1 — the pitch's opening scene, made playable.
 *
 * Three lanes into the chest. The left one is *painted* deadly and is perfectly
 * safe. The middle one looks clean and is the kill: marbles take your brakes
 * away right before a spring aimed back at the front door. The right lane is
 * genuinely safe, and the only guard on it is a swatter you have to time.
 *
 * So the taunt — "你肯定先踩左边" — is doing real work: it pushes you off the
 * fake trap and straight into the real one.
 */
const firstNight = build(
  'demo-lin',
  '小林的第一夜',
  '小林',
  '你肯定先踩左边。',
  { x: 6, z: 9 },
  [
    ['brick', 4, 5],
    ['brick', 4, 6],
    ['brick', 4, 7],
    ['brick', 4, 8],
    ['brick', 8, 5],
    ['brick', 8, 6],
    ['brick', 8, 7],
    ['brick', 8, 8],
    ['glue', 6, 4],
    ['bluff', 5, 6],
    ['bluff', 5, 7],
    ['marbles', 6, 6],
    // Aimed back at the door: charging the middle sends you home.
    ['spring', 6, 7, 2],
    ['decoy', 5, 9],
    ['swatter', 7, 9, 1],
  ],
)

/**
 * Room 2 — no corridor at all. A hole in the middle of the obvious line, a fan
 * that quietly rewrites your direction, and the chest tucked behind a wall so
 * the only way in is past a swatter on the beat.
 */
const revenge = build(
  'demo-jie',
  '阿杰的回礼',
  '阿杰',
  '这次我不骗你，我抬你过去。',
  { x: 2, z: 10 },
  [
    ['pit', 6, 6],
    ['fan', 4, 3, 3],
    ['spring', 9, 5, 1],
    ['bluff', 5, 4],
    ['bluff', 7, 4],
    ['marbles', 5, 9],
    ['marbles', 4, 9],
    ['brick', 3, 9],
    ['brick', 3, 10],
    ['swatter', 3, 11, 1],
    ['decoy', 8, 10],
  ],
)

export const DEMO_ROOMS: RoomDef[] = [firstNight, revenge]

export function demoRoom(index = 0): RoomDef {
  return DEMO_ROOMS[index % DEMO_ROOMS.length]!
}
